import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { IFetchLaboratoryTestsParams, ILaboratoryTest } from '~/interfaces'
import { HttpStatusCode } from '~/protocols/http'
import { fetchLaboratoryTests, message } from '~/services'
import { hasObjectValue } from '~/utils'

const UNIQUE_KEY = 'laboratoryTests'

export const useFetchLaboratoryTests = (
  fetchParams: IFetchLaboratoryTestsParams | null,
  config?: UseQueryOptions<ILaboratoryTest[], Error, ILaboratoryTest[]>
): UseQueryResult<ILaboratoryTest[], Error> => {
  const hasFetchParams = hasObjectValue({ ...fetchParams })

  const fetcher = async (): Promise<ILaboratoryTest[]> => {
    if (!fetchParams) throw new Error('fetchParams is required')

    try {
      const response = await fetchLaboratoryTests(fetchParams)
      const isResponseEmpty = response.length === 0

      if (isResponseEmpty) {
        throw message('detailsMedicalEvents.emptyResponseFetched')
      }

      return response
    } catch (error) {
      const axiosError = error as AxiosError

      if (typeof error === 'string') throw new Error(error)
      if (axiosError?.code === String(HttpStatusCode.notFound)) {
        throw new Error(message('detailsMedicalEvents.emptyResponseFetched'))
      }
      throw new Error(message('messages.genericUnexpectedError'))
    }
  }

  return useQuery<ILaboratoryTest[], Error>([UNIQUE_KEY, fetchParams], fetcher, {
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: hasFetchParams,
    ...config
  })
}
